import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { PlusCircle } from 'lucide-react';
import { useBots } from '@/contexts/BotsContext';
import { DashboardLayout } from '../components/layouts/DashboardLayout';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
import { StatsCards } from '../components/dashboard/StatsCards';
import { PerformanceChart } from '../components/dashboard/PerformanceChart';
import { ActiveBotsTable } from '../components/dashboard/ActiveBotsTable';
import { BotCard } from '../components/BotCard';
import { Button } from '../ui/Button';

interface DashboardPageProps {
  activeTab?: 'dashboard' | 'my-bots';
}

export const DashboardPage: React.FC<DashboardPageProps> = ({ activeTab = 'dashboard' }) => {
  const { bots, practiceBots, toggleBotStatus, editBot, clearCurrentBot, deleteBot, isLoading, fetchBots } = useBots();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<string | null>(null);
  
  // Single fetchBots call on mount - use from context
  React.useEffect(() => {
    fetchBots();
  }, [fetchBots]);
  
  // If bots are still loading, display a loading indicator
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 pt-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading dashboard...</p>
          <p className="text-sm text-gray-500 dark:text-gray-500 mt-2">
            If this takes too long, please check your internet connection
          </p>
        </div>
      </div>
    );
  }

  const handleCreateNewBot = () => {
    clearCurrentBot();
    console.log('[DASHBOARD] Navigating to create-bot page');
    navigate('/create-bot');
  };

  const handleEditBot = async (botId: string) => {
    await editBot(botId);
    navigate('/create-bot');
  };
  
  const handleToggleBotStatus = async (botId: string) => {
    setLoading(botId);
    try {
      await toggleBotStatus(botId);
    } catch (error) {
      console.error('Error toggling bot status:', error);
    } finally {
      setLoading(null);
    }
  };
  
  const handleDeleteBot = async (botId: string, isPractice = false) => {
    try {
      await deleteBot(botId);
    } catch (error) {
      console.error('[DASHBOARD] Delete error:', error);
    }
  };

  const renderMyBots = () => {
    // Saugumo patikra, jeigu bots arba practiceBots undefined
    const customBots = (bots ?? []).filter(bot => !bot.is_practice && !bot.is_from_marketplace);
    const marketplaceBots = (bots ?? []).filter(bot => !bot.is_practice && bot.is_from_marketplace && !bot.is_premium);
    const premiumBots = (bots ?? []).filter(bot => !bot.is_practice && bot.is_from_marketplace && bot.is_premium);

    return (
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        className="space-y-12"
      >
        {/* Header with Create Bot Button */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">My Bots</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage all your trading bots. Create new bots or activate them from successful backtests.
            </p>
          </div>
          <Button 
            variant="primary" 
            icon={<PlusCircle size={16} />}
            iconPosition="left"
            onClick={handleCreateNewBot}
          >
            Create New Bot
          </Button>
        </div>

        {/* Custom Bots */}
        <div>
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">My Custom Bots</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Bots you've created manually or activated from backtest results
              </p>
            </div>
          </div>

          {customBots.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {customBots.map((bot, index) => (
                <motion.div
                  key={bot.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  data-bot-id={bot.id}
                >
                  <BotCard
                    bot={bot}
                    onClick={() => handleEditBot(bot.id)}
                    onDelete={() => handleDeleteBot(bot.id, false)}
                    index={index}
                    variant="my-bots"
                  />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                No custom bots yet. Create your first bot or activate one from successful backtests!
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button 
                  variant="primary" 
                  onClick={handleCreateNewBot}
                  icon={<PlusCircle size={16} />}
                  iconPosition="left"
                >
                  Create New Bot
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/backtests')}
                >
                  View Backtests
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Practice Bots */}
        <div>
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Practice Bots</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Test your strategies risk-free with paper trading</p>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              icon={<PlusCircle size={16} />}
              iconPosition="left"
              onClick={handleCreateNewBot}
            >
              New Practice Bot
            </Button>
          </div>

          {(practiceBots ?? []).length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {practiceBots.map((bot, index) => (
                <BotCard
                  key={bot.id}
                  bot={bot}
                  onClick={() => handleEditBot(bot.id)}
                  onDelete={() => handleDeleteBot(bot.id, true)}
                  index={customBots.length + index}
                  variant="my-bots"
                />
              ))}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center">
              <p className="text-gray-600 dark:text-gray-400">No practice bots yet. Create a practice bot to test strategies!</p>
            </div>
          )}
        </div>

        {/* Marketplace Bots */}
        <div>
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Marketplace Bots</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Bots from our marketplace</p>
            </div>
          </div>

          {marketplaceBots.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {marketplaceBots.map((bot, index) => (
                <BotCard
                  key={bot.id}
                  bot={bot}
                  onClick={() => handleEditBot(bot.id)}
                  onDelete={() => handleDeleteBot(bot.id, false)}
                  index={customBots.length + (practiceBots?.length ?? 0) + index}
                  variant="my-bots"
                />
              ))}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center">
              <p className="text-gray-600 dark:text-gray-400">No marketplace bots. Visit the marketplace to discover bots!</p>
            </div>
          )}
        </div>

        {/* Premium Bots */}
        <div>
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Premium Bots</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Premium bots from our marketplace</p>
            </div>
          </div>

          {premiumBots.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {premiumBots.map((bot, index) => (
                <BotCard
                  key={bot.id}
                  bot={bot}
                  onClick={() => handleEditBot(bot.id)}
                  onDelete={() => handleDeleteBot(bot.id, false)}
                  index={customBots.length + (practiceBots?.length ?? 0) + marketplaceBots.length + index}
                  variant="my-bots"
                />
              ))}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center">
              <p className="text-gray-600 dark:text-gray-400">No premium bots. Upgrade your plan to access premium bots!</p>
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <DashboardLayout>
      {activeTab === 'dashboard' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="space-y-8"
        >
          <DashboardHeader onCreateBot={handleCreateNewBot} />
          <StatsCards />
          <PerformanceChart />
          <ActiveBotsTable
            bots={bots ?? []}
            loading={loading}
            onToggleStatus={handleToggleBotStatus}
            onEditBot={handleEditBot}
          />
        </motion.div>
      )}

      {activeTab === 'my-bots' && renderMyBots()}
    </DashboardLayout>
  );
};

// **BŪTINAI!** Default eksportas - dėl jo NEBEBUS klaidos
export default DashboardPage;